import { FloatingTextEffect } from '../../renderer/skillVisualEffects/FloatingTextEffect';
import { FireballEffect } from '../../renderer/skillVisualEffects/FireballEffect';
import type { TileMapRenderer } from '../../renderer/tilemap/TileMapRenderer';
import type { GameState } from '../../model/gamestate/GameState';
import type { MapEventData } from '../../spacetime/mapEvents';
import { MAP_EVENT_TYPE } from '../../spacetime/mapEvents';
import type { DebugWindow } from '../../ui/DebugWindow';
import type { EventHub } from '../../events/EventHub';
import { INTENT_FAILED_EVENT } from '../../events/IntentFailedEvent';

interface MapEventPlaybackParams {
  playerId: string;
  gameState: GameState;
  renderer: TileMapRenderer;
  debugWindow: DebugWindow;
  eventHub: EventHub;
}

export interface MapEventPlaybackController {
  start(): void;
  stop(): void;
  handleEvents(events: MapEventData[]): void;
}

export function createMapEventPlaybackController(
  params: MapEventPlaybackParams
): MapEventPlaybackController {
  const { playerId, gameState, renderer, debugWindow, eventHub } = params;

  let seenMapEventIds = new Set<string>();
  let mapEventHistory: MapEventData[] = [];
  let hiddenAtTick: number | null = null;
  let skipVisualEffectsThroughTick: number | null = null;
  const MAX_MAP_EVENT_HISTORY = 200;
  const supportsVisibilityApi =
    typeof document !== 'undefined' &&
    typeof document.hidden === 'boolean' &&
    typeof document.addEventListener === 'function' &&
    typeof document.removeEventListener === 'function';

  const shouldRenderVisualEffect = (eventTick: number): boolean => {
    if (supportsVisibilityApi && document.hidden) {
      return false;
    }
    if (skipVisualEffectsThroughTick !== null && eventTick <= skipVisualEffectsThroughTick) {
      return false;
    }
    return true;
  };

  const handleVisibilityChange = (): void => {
    if (!supportsVisibilityApi) {
      return;
    }
    if (document.hidden) {
      hiddenAtTick = gameState.getTick();
      return;
    }

    const resumedAtTick = gameState.getTick();
    if (hiddenAtTick !== null) {
      skipVisualEffectsThroughTick = Math.max(skipVisualEffectsThroughTick ?? 0, resumedAtTick);
    }
    hiddenAtTick = null;
  };

  const trimHistory = (): void => {
    if (mapEventHistory.length <= MAX_MAP_EVENT_HISTORY) {
      return;
    }

    const toDrop = mapEventHistory.length - MAX_MAP_EVENT_HISTORY;
    const removed = mapEventHistory.splice(0, toDrop);
    for (const event of removed) {
      seenMapEventIds.delete(event.eventId);
    }
  };

  const handleEvent = (event: MapEventData): void => {
    if (event.eventType === MAP_EVENT_TYPE.intentFailed && event.sourceId === playerId) {
      console.warn(`[intent failed] ${event.skillId}: ${event.payloadJson}`);
      eventHub.emit(INTENT_FAILED_EVENT, { actionId: event.skillId, reason: event.payloadJson });
    }

    if (event.eventType === MAP_EVENT_TYPE.move && event.snap) {
      renderer.markActorSnap(event.sourceId, { x: event.targetX, y: event.targetY });
    }

    if (event.eventType === MAP_EVENT_TYPE.animation && shouldRenderVisualEffect(event.tick)) {
      renderer.playAnimation(event.skillId, event.payloadJson);
    }

    if (
      event.eventType === MAP_EVENT_TYPE.skillUse &&
      shouldRenderVisualEffect(event.tick) &&
      (event.skillId === 'fire_blast' ||
        event.skillId === 'fireball' ||
        event.skillId === 'ice_shard' ||
        event.skillId === 'ice_ball')
    ) {
      const isIceSkill = event.skillId === 'ice_shard' || event.skillId === 'ice_ball';
      renderer.addEffect(new FireballEffect({
        fromX: event.sourceX,
        fromY: event.sourceY,
        toX: event.targetX,
        toY: event.targetY,
        tileToScreenCenter: (x, y) => renderer.worldToScreenCenter(x, y),
        colors: isIceSkill ? {
          core: 'rgba(224, 242, 254, 0.98)',
          mid: 'rgba(125, 211, 252, 0.95)',
          outer: 'rgba(56, 189, 248, 0)',
          impactCore: 'rgba(240, 249, 255, 0.95)',
          impactMid: 'rgba(147, 197, 253, 0.85)',
          impactOuter: 'rgba(56, 189, 248, 0)',
        } : undefined,
      }));
    }

    if (
      event.eventType === MAP_EVENT_TYPE.damage &&
      event.amount !== 0 &&
      shouldRenderVisualEffect(event.tick)
    ) {
      const screen = renderer.worldToScreenCenter(event.targetX, event.targetY);
      renderer.addEffect(new FloatingTextEffect({
        text: event.amount < 0 ? `+${Math.abs(event.amount)}` : `-${event.amount}`,
        amount: event.amount,
        screenX: screen.x,
        screenY: screen.y,
      }));
    }

    if (
      event.eventType === MAP_EVENT_TYPE.heal &&
      event.amount > 0 &&
      shouldRenderVisualEffect(event.tick)
    ) {
      const screen = renderer.worldToScreenCenter(event.targetX, event.targetY);
      renderer.addEffect(new FloatingTextEffect({
        text: `+${event.amount}`,
        amount: -event.amount,
        screenX: screen.x,
        screenY: screen.y,
      }));
    }
  };

  return {
    start(): void {
      if (supportsVisibilityApi) {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        handleVisibilityChange();
      }
    },

    stop(): void {
      seenMapEventIds = new Set<string>();
      mapEventHistory = [];
      hiddenAtTick = null;
      skipVisualEffectsThroughTick = null;
      if (supportsVisibilityApi) {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    },

    handleEvents(events: MapEventData[]): void {
      let appended = false;

      for (const event of events) {
        if (seenMapEventIds.has(event.eventId)) {
          continue;
        }
        seenMapEventIds.add(event.eventId);
        mapEventHistory.push(event);
        appended = true;
        handleEvent(event);
      }

      if (!appended) {
        return;
      }

      trimHistory();
      debugWindow.updateMapEvents(mapEventHistory);
    },
  };
}
